<template>
    <div class="mt-3 rounded-md border border-gray-200 bg-gray-50 p-4">
        <div class="mb-4">
            <h4 class="text-sm font-semibold text-gray-900">
                Location Assets
            </h4>

            <p class="text-xs text-gray-500">
                Assign experience assets to this asset location.
            </p>
        </div>

        <div
            v-if="error"
            class="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
            {{ error }}
        </div>

        <div class="flex items-end gap-3">
            <div class="flex-1">
                <label
                    class="mb-1 block text-sm font-medium text-gray-700"
                >
                    Experience Asset
                </label>

                <select
                    v-model="selectedExperienceAssetId"
                    class="w-full rounded-md border border-gray-300 px-3 py-2"
                    :disabled="
                        loadingAvailable
                        || saving
                        || !availableAssets.length
                    "
                >
                    <option value="">
                        {{
                            loadingAvailable
                                ? 'Loading assets...'
                                : availableAssets.length
                                    ? 'Select an asset'
                                    : 'No assets available'
                        }}
                    </option>

                    <option
                        v-for="relationship in availableAssets"
                        :key="relationship.id"
                        :value="relationship.id"
                    >
                        {{ assetLabel(relationship) }}
                    </option>
                </select>
            </div>

            <button
                type="button"
                class="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="
                    !selectedExperienceAssetId
                    || saving
                "
                @click="attachAsset"
            >
                {{ saving ? 'Adding...' : 'Add Asset' }}
            </button>
        </div>

        <div
            v-if="
                validationErrors
                    .experience_lite_integration_asset_id
            "
            class="mt-2 text-sm text-red-600"
        >
            {{
                validationErrors
                    .experience_lite_integration_asset_id[0]
            }}
        </div>

        <div
            v-if="loadingAttached"
            class="py-6 text-center text-sm text-gray-500"
        >
            Loading location assets...
        </div>

        <div
            v-else-if="!attachedAssets.length"
            class="mt-4 rounded-md border border-gray-200 bg-white p-5 text-center text-sm text-gray-500"
        >
            No assets are assigned to this location.
        </div>

        <div
            v-else
            class="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white"
        >
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th
                            class="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500"
                        >
                            Asset
                        </th>

                        <th
                            class="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500"
                        >
                            Roblox Asset ID
                        </th>

                        <th
                            class="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500"
                        >
                            Actions
                        </th>
                    </tr>
                </thead>

                <tbody class="divide-y divide-gray-200">
                    <tr
                        v-for="relationship in attachedAssets"
                        :key="relationship.id"
                    >
                        <td
                            class="px-4 py-3 text-sm font-medium text-gray-900"
                        >
                            {{ attachedAssetName(relationship) }}
                        </td>

                        <td
                            class="px-4 py-3 text-sm text-gray-600"
                        >
                            {{
                                attachedRobloxAssetId(
                                    relationship
                                )
                            }}
                        </td>

                        <td class="px-4 py-3 text-right">
                            <button
                                type="button"
                                class="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                :disabled="
                                    deletingId ===
                                    relationship
                                        .experience_lite_integration_asset_id
                                "
                                @click="detachAsset(relationship)"
                            >
                                {{
                                    deletingId ===
                                    relationship
                                        .experience_lite_integration_asset_id
                                        ? 'Removing...'
                                        : 'Remove'
                                }}
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
export default {
    name: 'LiteIntegrationAssetLocationAssets',

    props: {
        liteIntegration: {
            type: Object,
            required: true,
        },

        liteIntegrationAssetLocation: {
            type: Object,
            required: true,
        },
        experienceAssetsRevision: {
            type: Number,
            required: true,
        },
    },

    watch: {
        experienceAssetsRevision() {
            this.load();
        },
    },

    data() {
        return {
            attachedAssets: [],
            availableAssets: [],
            selectedExperienceAssetId: '',
            validationErrors: {},
            loadingAttached: false,
            loadingAvailable: false,
            saving: false,
            deletingId: null,
            error: null,
        };
    },

    computed: {
        baseUrl() {
            return `/lite-integrations/${this.liteIntegration.uuid}`
                + `/asset-locations/${this.liteIntegrationAssetLocation.uuid}`
                + '/assets';
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        async load() {
            await Promise.all([
                this.loadAttachedAssets(),
                this.loadAvailableAssets(),
            ]);
        },

        async loadAttachedAssets() {
            this.loadingAttached = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    this.baseUrl
                );

                this.attachedAssets =
                    response.data.data ?? [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load location assets.'
                );
            } finally {
                this.loadingAttached = false;
            }
        },

        async loadAvailableAssets() {
            this.loadingAvailable = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    `${this.baseUrl}/available`
                );

                this.availableAssets =
                    response.data.data ?? [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load available assets.'
                );
            } finally {
                this.loadingAvailable = false;
            }
        },

        async attachAsset() {
            if (!this.selectedExperienceAssetId) {
                return;
            }

            this.saving = true;
            this.error = null;
            this.validationErrors = {};

            try {
                await this.$http.post(
                    this.baseUrl,
                    {
                        experience_lite_integration_asset_id:
                            this.selectedExperienceAssetId,
                    }
                );

                this.selectedExperienceAssetId = '';

                await Promise.all([
                    this.loadAttachedAssets(),
                    this.loadAvailableAssets(),
                ]);
            } catch (error) {
                if (error.response?.status === 422) {
                    this.validationErrors =
                        error.response.data.errors ?? {};

                    return;
                }

                this.error = this.getErrorMessage(
                    error,
                    'Unable to attach asset to location.'
                );
            } finally {
                this.saving = false;
            }
        },

        async detachAsset(relationship) {
            const experienceAssetId =
                relationship
                    .experience_lite_integration_asset_id;

            const assetName =
                this.attachedAssetName(relationship);

            const confirmed = window.confirm(
                `Remove "${assetName}" from this asset location?`
            );

            if (!confirmed) {
                return;
            }

            this.deletingId = experienceAssetId;
            this.error = null;

            try {
                await this.$http.delete(
                    `${this.baseUrl}/${experienceAssetId}`
                );

                this.attachedAssets =
                    this.attachedAssets.filter(
                        item =>
                            item
                                .experience_lite_integration_asset_id
                            !== experienceAssetId
                    );

                await this.loadAvailableAssets();
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to remove asset from location.'
                );
            } finally {
                this.deletingId = null;
            }
        },

        assetLabel(relationship) {
            const asset =
                relationship.lite_integration_asset
                && relationship
                    .lite_integration_asset.asset
                    ? relationship
                        .lite_integration_asset.asset
                    : null;

            const name =
                asset && asset.name
                    ? asset.name
                    : asset && asset.uuid
                        ? asset.uuid
                        : 'Unknown asset';

            const robloxAssetId =
                relationship.roblox_asset_id;

            if (!robloxAssetId) {
                return name;
            }

            return `${name} (${robloxAssetId})`;
        },

        attachedAssetName(relationship) {
            const experienceAsset =
                relationship.experience_asset;

            if (
                !experienceAsset
                || !experienceAsset
                    .lite_integration_asset
                || !experienceAsset
                    .lite_integration_asset.asset
            ) {
                return 'Unknown asset';
            }

            const asset =
                experienceAsset
                    .lite_integration_asset.asset;

            return asset.name
                ?? asset.uuid
                ?? 'Unknown asset';
        },

        attachedRobloxAssetId(relationship) {
            if (
                !relationship.experience_asset
                || !relationship
                    .experience_asset.roblox_asset_id
            ) {
                return '-';
            }

            return relationship
                .experience_asset
                .roblox_asset_id;
        },

        getErrorMessage(error, fallback) {
            return error.response?.data?.message
                ?? fallback;
        },
    },
};
</script>